import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { useApi } from 'contexts/ApiContext';

import type { api } from 'api';
import type { HTTPError } from 'api/error';
import type { MergeBranchPayload } from 'api/resources/draft/types';
import { curryPostFunction } from 'api/utils';
import { generateProjectChangeLogStreamKey } from 'hooks/api/changeLogStream/useProjectChangeLog';
import { useDeploymentsQueryKey } from 'hooks/api/deployments/hooks';
import useProjectRoute from 'hooks/useProjectRoute';
import { useToast } from 'hooks/useToast';
import { getBranchesQueryKey } from './useBranches';

type FunctionArgs = Omit<MergeBranchPayload, 'accountId' | 'projectId'>;

const useMergeDraft = (
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof api.mergeBranch>>,
    HTTPError,
    FunctionArgs
  >,
) => {
  const api = useApi();
  const toast = useToast();

  const { accountId, projectId } = useProjectRoute();

  const { queryKey } = useDeploymentsQueryKey();
  const projectChangeLogQueryKey = generateProjectChangeLogStreamKey(
    accountId,
    projectId,
  );
  const branchesQueryKey = getBranchesQueryKey(accountId, projectId);

  return useMutation(
    curryPostFunction(api.methods.mergeBranch)({ accountId, projectId }),
    {
      retry: false,
      onError: () => {
        toast.error({
          title: 'Error merging branch',
          description:
            'There was an error merging the branch, please refresh the page and try again.',
        });
      },
      meta: {
        invalidates: [queryKey, projectChangeLogQueryKey, branchesQueryKey],
      },
      ...options,
    },
  );
};

export default useMergeDraft;
